/**
 * XRAdminPage — v10.8.0
 * Mantenedor SimuClinic XR con 5 tabs: Dispositivos, Marcadores, Zonas,
 * Procedimientos, Recursos. Feature-flag protected.
 */
import { useEffect, useState } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DeviceMobile, MapPin, Path, Cube, Plus, Trash, Cube as CubeIcon, Info, Textbox, Printer, X } from '@phosphor-icons/react';
import XRAssetLibrary from './XRAssetLibrary';

const DEVICE_TYPES = [
  { value: 'mobile_ar', label: 'Móvil AR' },
  { value: 'tablet_ar', label: 'Tablet AR' },
  { value: 'mixed_reality', label: 'Realidad Mixta (Quest)' },
  { value: 'ar_glasses', label: 'Gafas AR (XREAL)' },
];

const MARKER_TYPES = ['patient', 'anatomical_zone', 'medication', 'equipment', 'device', 'location', 'action', 'resource'];
const ASSET_TYPES = ['3d_model', 'image', 'video', 'label', 'panel', 'animation', 'effect'];

export default function XRAdminPage() {
  const [flag, setFlag] = useState(null);

  useEffect(() => {
    api.get('/xr/feature-flag').then((r) => setFlag(r.data)).catch(() => setFlag({ effective: false }));
  }, []);

  if (!flag) return <div className="p-8 text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="xr-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CubeIcon size={26} className="text-cyan-600" weight="fill" />
            SimuClinic XR — Mantenedores
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Dispositivos, marcadores, zonas anatómicas, procedimientos y recursos AR/MR.
          </p>
        </div>
        <div className="text-right">
          <Badge className={flag.effective ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>
            XR {flag.effective ? 'activo' : 'inactivo'} · Global {flag.xr_enabled_global ? 'ON' : 'OFF'}
          </Badge>
        </div>
      </div>

      {!flag.effective && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <Info size={20} className="text-amber-600 mt-0.5" weight="fill" />
            <div className="text-sm text-amber-900">
              XR está deshabilitado. Puedes configurar todo aquí; los escenarios lo verán solo cuando actives <strong>xr_enabled</strong> en Ajustes → Feature Flags.
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="devices" className="gap-2"><DeviceMobile size={16} /> Dispositivos</TabsTrigger>
          <TabsTrigger value="markers" className="gap-2"><MapPin size={16} /> Marcadores</TabsTrigger>
          <TabsTrigger value="zones" className="gap-2"><Textbox size={16} /> Zonas</TabsTrigger>
          <TabsTrigger value="procedures" className="gap-2"><Path size={16} /> Procedimientos</TabsTrigger>
          <TabsTrigger value="assets" className="gap-2"><Cube size={16} /> Recursos</TabsTrigger>
        </TabsList>

        <TabsContent value="devices"><DevicesTab /></TabsContent>
        <TabsContent value="markers"><MarkersTab /></TabsContent>
        <TabsContent value="zones"><ZonesTab /></TabsContent>
        <TabsContent value="procedures"><ProceduresTab /></TabsContent>
        <TabsContent value="assets"><XRAssetLibrary /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ Devices ============ */
function DevicesTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', device_type: 'mobile_ar', identifier: '', notes: '' });
  const load = async () => {
    const r = await api.get('/xr/devices'); setRows(r.data?.devices || []);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) return toast.error('Nombre requerido');
    try { await api.post('/xr/devices', form); toast.success('Dispositivo creado'); setForm({ name: '', device_type: 'mobile_ar', identifier: '', notes: '' }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/xr/devices/${id}`); await load(); };

  return (
    <Card>
      <CardHeader><CardTitle>Dispositivos XR registrados</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 rounded-md bg-slate-50 border border-slate-100">
          <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="device-name" /></div>
          <div><Label className="text-xs">Tipo</Label>
            <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEVICE_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Identificador</Label><Input value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} /></div>
          <div className="md:col-span-1"><Label className="text-xs">Notas</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button onClick={create} data-testid="create-device-btn"><Plus size={14} className="mr-1" />Añadir</Button>
        </div>
        <div className="space-y-1">
          {rows.length === 0 && <div className="text-slate-400 text-sm text-center py-4">Sin dispositivos aún.</div>}
          {rows.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-white">
              <DeviceMobile size={18} className="text-cyan-600" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{d.name}</div>
                <div className="text-xs text-slate-500">
                  {DEVICE_TYPES.find((t) => t.value === d.device_type)?.label || d.device_type} · {d.identifier || 'sin id'}
                </div>
              </div>
              <Badge className={d.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>{d.active ? 'Activo' : 'Inactivo'}</Badge>
              <Button size="icon" variant="ghost" onClick={() => remove(d.id)} className="text-red-600"><Trash size={14} /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============ Markers ============ */
function MarkersTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ marker_id: '', marker_type: 'anatomical_zone', label: '' });
  const [showPrint, setShowPrint] = useState(false);
  const load = async () => { const r = await api.get('/xr/markers'); setRows(r.data?.markers || []); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.marker_id) return toast.error('marker_id requerido');
    try { await api.post('/xr/markers', form); toast.success('Marcador guardado'); setForm({ marker_id: '', marker_type: 'anatomical_zone', label: '' }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (id) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/xr/markers/${id}`); await load(); };
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Marcadores</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowPrint(true)} disabled={rows.length === 0} data-testid="print-markers-btn">
              <Printer size={14} className="mr-1" /> Imprimir QR
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end p-3 rounded-md bg-slate-50 border border-slate-100">
            <div><Label className="text-xs">Marker ID</Label><Input value={form.marker_id} onChange={(e) => setForm({ ...form, marker_id: e.target.value })} placeholder="M001" data-testid="marker-id" /></div>
            <div><Label className="text-xs">Tipo</Label>
              <Select value={form.marker_type} onValueChange={(v) => setForm({ ...form, marker_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MARKER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Etiqueta</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
            <Button onClick={create} data-testid="create-marker-btn"><Plus size={14} className="mr-1" />Añadir</Button>
          </div>
          <div className="space-y-1">
            {rows.length === 0 && <div className="text-slate-400 text-sm text-center py-4">Sin marcadores.</div>}
            {rows.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-white">
                <MapPin size={16} className="text-cyan-600" />
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">{m.marker_id}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{m.label || <em className="text-slate-400">sin etiqueta</em>}</div>
                  <div className="text-xs text-slate-500">{m.marker_type}{m.scenario_id ? ` · scenario ${m.scenario_id.slice(0, 8)}` : ' · global'}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove(m.id)} className="text-red-600"><Trash size={14} /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {showPrint && <PrintableMarkersSheet markers={rows} onClose={() => setShowPrint(false)} />}
    </>
  );
}

/* ============ Printable QR Sheet ============ */
function PrintableMarkersSheet({ markers, onClose }) {
  const doPrint = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" data-testid="printable-markers-sheet">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
      <div className="no-print sticky top-0 flex items-center justify-between p-3 bg-white border-b border-slate-200 shadow-sm">
        <div className="font-semibold text-slate-900">Hoja imprimible · {markers.length} marcadores</div>
        <div className="flex items-center gap-2">
          <Button onClick={doPrint} data-testid="do-print-btn"><Printer size={14} className="mr-1" /> Imprimir</Button>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={16} /></Button>
        </div>
      </div>
      <div className="p-8 max-w-[210mm] mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">SimuClinic XR · Marcadores</h1>
        <p className="text-sm text-slate-500 mb-6">Pega cada QR sobre el maniquí, equipamiento o zona correspondiente. Cada QR codifica el `marker_id`.</p>
        <div className="grid grid-cols-2 gap-6">
          {markers.map((m) => (
            <div key={m.id} className="border border-slate-300 rounded-lg p-4 flex items-center gap-4 break-inside-avoid">
              <div className="p-2 bg-white border border-slate-200 rounded">
                <QRCodeSVG value={`xr:${m.marker_id}`} size={110} level="M" includeMargin={false} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{m.marker_type}</div>
                <div className="text-lg font-bold text-slate-900 truncate">{m.label || m.marker_id}</div>
                <div className="text-xs font-mono text-cyan-700 mt-1">{m.marker_id}</div>
                <div className="text-[10px] text-slate-400 mt-2">xr:{m.marker_id}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ Zones ============ */
function ZonesTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: '', label: '', body_region: '' });
  const load = async () => { const r = await api.get('/xr/zones'); setRows(r.data?.zones || []); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.code || !form.label) return toast.error('Código y etiqueta requeridos');
    try { await api.post('/xr/zones', form); toast.success('Zona guardada'); setForm({ code: '', label: '', body_region: '' }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (code) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/xr/zones/${code}`); await load(); };
  return (
    <Card>
      <CardHeader><CardTitle>Zonas anatómicas / interacción</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end p-3 rounded-md bg-slate-50 border border-slate-100">
          <div><Label className="text-xs">Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="chest" data-testid="zone-code" /></div>
          <div><Label className="text-xs">Etiqueta</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Tórax" /></div>
          <div><Label className="text-xs">Región</Label><Input value={form.body_region} onChange={(e) => setForm({ ...form, body_region: e.target.value })} placeholder="anterior" /></div>
          <Button onClick={create} data-testid="create-zone-btn"><Plus size={14} className="mr-1" />Añadir</Button>
        </div>
        <div className="space-y-1">
          {rows.map((z) => (
            <div key={z.code} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-white">
              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{z.code}</span>
              <div className="flex-1"><div className="text-sm font-medium">{z.label}</div><div className="text-xs text-slate-500">{z.body_region}</div></div>
              <Button size="icon" variant="ghost" onClick={() => remove(z.code)} className="text-red-600"><Trash size={14} /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============ Procedures ============ */
function ProceduresTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: '', name: '', description: '', required_order: true, allowed_time_seconds: 300 });
  const load = async () => { const r = await api.get('/xr/procedures'); setRows(r.data?.procedures || []); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.code || !form.name) return toast.error('Código y nombre requeridos');
    try { await api.post('/xr/procedures', { ...form, allowed_time_seconds: Number(form.allowed_time_seconds) || null, steps: [] }); toast.success('Procedimiento guardado'); setForm({ code: '', name: '', description: '', required_order: true, allowed_time_seconds: 300 }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (code) => { if (!window.confirm('¿Eliminar?')) return; await api.delete(`/xr/procedures/${code}`); await load(); };
  return (
    <Card>
      <CardHeader><CardTitle>Procedimientos guiados</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end p-3 rounded-md bg-slate-50 border border-slate-100">
          <div><Label className="text-xs">Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ECG_ELEC" /></div>
          <div><Label className="text-xs">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Colocación electrodos" /></div>
          <div><Label className="text-xs">Tiempo (s)</Label><Input type="number" value={form.allowed_time_seconds} onChange={(e) => setForm({ ...form, allowed_time_seconds: e.target.value })} /></div>
          <div><Label className="text-xs">Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <Button onClick={create}><Plus size={14} className="mr-1" />Añadir</Button>
        </div>
        <div className="space-y-1">
          {rows.map((p) => (
            <div key={p.code} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 bg-white">
              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{p.code}</span>
              <div className="flex-1"><div className="text-sm font-medium">{p.name}</div><div className="text-xs text-slate-500">{(p.steps || []).length} pasos · {p.allowed_time_seconds || 'sin'} s</div></div>
              <Button size="icon" variant="ghost" onClick={() => remove(p.code)} className="text-red-600"><Trash size={14} /></Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============ Assets tab reemplazado por XRAssetLibrary (v10.11.0) ============ */
